import { ParamsProps } from "components/live-event/live-event";
import { GetSessionStaticVideos } from "connection/session-static-videos";
import { useSocket } from "connection/socket";
import { getVideo } from "connection/videos";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTypedSelector } from "store/reducers/use-typed-selector";
import { Session, StaticVideo } from "types/working-model";
import { useGetEvent } from "./session.hooks";
import { useIsAdmin } from "./context.hooks";
import { showAlert } from "@general-ui/alert/alert-service";

export const useSessionStaticVideos = (session: Session | null, language: string) => {
	const token = useTypedSelector(state => state.AuthReducer.token);
	const user = useTypedSelector(state => state.AuthReducer.user);
	const eventUuid = useGetEvent()?.uuid;
	const isAdmin = useIsAdmin();

	const [loadingVideos, setLoadingVideos] = useState(false);
	const [videoMap, setVideoMap] = useState<Record<string, StaticVideo>>({}); // Language -> StaticVideo
	// This ref prevents hitting the lamdba on event publish since we don't want every user to do that at once
	const fetchedSessionUuid = useRef<string | null>(null);

	const { sessionUuid }: ParamsProps = useParams();

	const socket = useSocket(`session-${sessionUuid}`);

	useEffect(() => {
		const fetchStaticVideoMap = async () => {
			if (eventUuid && session?.uuid) {
				setLoadingVideos(true);
				// Only use missing videos for admin/editor
				const missingVideos = videosHasUpdated(session.static_videos ?? {}, videoMap);
				if (isAdmin && missingVideos) {
					if (!session.static_videos || !token || !user) return;
					const videos: Record<string, StaticVideo> = {};
					fetchedSessionUuid.current = session.uuid;
					for (const [language, uuid] of Object.entries(session.static_videos)) {
						try {
							const video = await getVideo(token, user.active_channel, uuid);
							videos[language] = video;
						} catch {
							showAlert({
								message: `Failed to fetch on demand video for language ${language}`,
								type: 'error',
								duration: 5000,
							})
						}
					}
					setVideoMap(videos);
				} else if (session.uuid != fetchedSessionUuid.current) {
					const videos = await GetSessionStaticVideos(eventUuid, session.uuid);
					setVideoMap(videos);
					fetchedSessionUuid.current = session.uuid;
				}
				setLoadingVideos(false);
			}
		}

		if (eventUuid && session?.uuid && !loadingVideos) {
			fetchStaticVideoMap();
		}
	}, [session?.uuid, session?.static_videos, token, user, isAdmin, eventUuid, loadingVideos]);


	useEffect(() => {
		const updateStaticVideos = ({ data }: { data: Record<string, StaticVideo> }) => {
			if (data && !isAdmin) {
				setVideoMap(data);
			}
		}
		socket.addListener('static-videos-updated', updateStaticVideos);
		return () => {
			socket.removeListener('static-videos-updated', updateStaticVideos);
		}
	}, [session?.uuid, session?.static_videos, token, user, isAdmin, socket, loadingVideos]);

	let currentVideo;

	const lang = session?.streaming_options.single_stream ? session?.default_language : language;

	if (session) {
		currentVideo = videoMap[lang] ?? videoMap[session?.default_language] ?? null;
	}

	return {
		loadingVideos,
		currentVideo
	}
}


const videosHasUpdated = (staticVideos: Record<string, string>, videoMap: Record<string, StaticVideo>) => {
	if (Object.entries(staticVideos ?? {}).length !== Object.entries(videoMap).length) {
		return true;
	}

	for (const [language, uuid] of Object.entries(staticVideos ?? {})) {
		if (videoMap[language]?.uuid !== uuid) {
			return true;
		}
	}
	return false;
}
